import React, { useState, useEffect } from 'react';
import { CareerFocusWithMetrics } from '../../hooks/useCareerFocus';
import { useDarkMode } from '../../context/DarkModeContext';
import { IoDocumentTextOutline, IoFunnelOutline, IoSearchOutline, IoLinkOutline, IoDocumentOutline, IoCardOutline } from 'react-icons/io5';
import { motion } from 'framer-motion';
import { useCareerCards } from '../../hooks/useCareerCards';
import { useGoals } from '../../hooks/useGoals';
import { supabase } from '../../utils/supabase';
import { useNotes } from '../../hooks/useNotes';
import EvidenceLinkingModal from './EvidenceLinkingModal';

interface EvidenceLibraryTabProps {
  focus: CareerFocusWithMetrics;
  cards: any[]; // Will use the proper type from useCareerCards
  cardsLoading: boolean;
  cardsError: Error | null;
  refreshCards: () => Promise<void>;
  activeRubric?: any; // Rubric data from parent
  onQuickAdd?: () => void;
}

interface EvidenceLink {
  id: string;
  source_id: string;
  target_id: string;
  target_type: 'goal' | 'rubric_item' | 'rubric_criterion';
  link_type: string;
  metadata?: any;
}

interface EnrichedCareerCard {
  id: string;
  title: string;
  summary: string;
  impact: string;
  created_at: string;
  linkedGoals: { id: string; text: string }[];
  linkedRubricItems: { categoryId: string; itemIndex: number; text: string }[];
  tags: string[];
}

interface EnrichedNote {
  id: string;
  title: string;
  content: string;
  created_at: string;
  linkedGoals: { id: string; text: string }[];
  linkedRubricItems: { categoryId: string; itemIndex: number; text: string }[];
  tags: string[];
}

const EvidenceLibraryTab: React.FC<EvidenceLibraryTabProps> = ({ 
  focus,
  cards,
  cardsLoading,
  cardsError,
  refreshCards,
  activeRubric,
  onQuickAdd
}) => {
  const { darkMode } = useDarkMode();
  const [activeTab, setActiveTab] = useState<'cards' | 'notes'>('cards');
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState<'all' | 'linked' | 'unlinked'>('all');
  const [selectedItemId, setSelectedItemId] = useState<string | null>(null);
  const [selectedItemType, setSelectedItemType] = useState<'career_card' | 'note' | null>(null);
  const [evidenceLinks, setEvidenceLinks] = useState<EvidenceLink[]>([]);
  const [enrichedCards, setEnrichedCards] = useState<EnrichedCareerCard[]>([]);
  const [enrichedNotes, setEnrichedNotes] = useState<EnrichedNote[]>([]);
  
  // Remove useCareerCards - we get cards from props now
  const { notes, error: notesError } = useNotes();
  const { goals } = useGoals(focus.id);

  const textColor = darkMode ? 'text-gray-100' : 'text-gray-900';
  const mutedTextColor = darkMode ? 'text-gray-400' : 'text-gray-600';
  const cardBg = darkMode ? 'bg-gray-700' : 'bg-gray-50';
  const borderColor = darkMode ? 'border-gray-600' : 'border-gray-200';
  const inputBg = darkMode ? 'bg-gray-800' : 'bg-white';
  const tabActiveBg = darkMode ? 'bg-gray-700' : 'bg-gray-200';
  const tabInactiveBg = darkMode ? 'bg-gray-800' : 'bg-gray-100';

  // Fetch evidence links
  useEffect(() => {
    if (!focus.id) return;

    const fetchLinks = async () => {
      const { data, error } = await supabase
        .from('content_links')
        .select('*')
        .in('source_type', ['career_card', 'note'])
        .in('target_type', ['goal', 'rubric_item', 'rubric_criterion']);
      
      if (error) {
        console.error('Error fetching evidence links:', error);
        setEvidenceLinks([]);
        return;
      }
      
      setEvidenceLinks(data || []);
    };

    fetchLinks();
  }, [focus.id]);

  // Enrich career cards with link information
  useEffect(() => {
    if (!cards || !goals) return;

    const enrichCards = async () => {
      const enriched = cards.map(card => {
        // Find linked goals
        const goalLinks = evidenceLinks.filter(
          link => link.source_id === card.id && link.target_type === 'goal'
        );
        const linkedGoals = goalLinks
          .map(link => {
            const goal = goals.find(g => g.id === link.target_id);
            return goal ? { id: goal.id, text: goal.goal_text } : null;
          })
          .filter(Boolean) as { id: string; text: string }[];

        // Find linked rubric criteria
        const rubricLinks = evidenceLinks.filter(
          link => link.source_id === card.id && link.target_type === 'rubric_criterion'
        );
        const linkedRubricItems = rubricLinks.map(link => ({
          categoryId: link.metadata?.category_name || '',
          itemIndex: 0,
          text: link.metadata?.rubric_text || 'Rubric criterion'
        }));

        return {
          id: card.id,
          title: card.title || card.card?.card_data?.title || 'Untitled',
          summary: card.card?.card_data?.summary || '',
          impact: card.card?.card_data?.impact || '',
          created_at: card.created_at || card.card?.created_at,
          linkedGoals,
          linkedRubricItems,
          tags: card.custom_tags || card.card?.card_data?.custom_tags || []
        };
      });

      setEnrichedCards(enriched);
    };

    enrichCards();
  }, [cards, goals, evidenceLinks]);

  // Enrich notes with link information
  useEffect(() => {
    if (!notes || !goals) return;

    const enrichNotes = async () => {
      const enriched = notes.map(note => {
        // Find linked goals
        const goalLinks = evidenceLinks.filter(
          link => link.source_id === note.id && link.target_type === 'goal'
        );
        const linkedGoals = goalLinks
          .map(link => {
            const goal = goals.find(g => g.id === link.target_id);
            return goal ? { id: goal.id, text: goal.goal_text } : null;
          })
          .filter(Boolean) as { id: string; text: string }[];

        // Find linked rubric criteria
        const rubricLinks = evidenceLinks.filter(
          link => link.source_id === note.id && link.target_type === 'rubric_criterion'
        );
        const linkedRubricItems = rubricLinks.map(link => ({
          categoryId: link.metadata?.category_name || '',
          itemIndex: 0,
          text: link.metadata?.rubric_text || 'Rubric criterion'
        }));

        return {
          id: note.id,
          title: note.title,
          content: note.content || '',
          created_at: note.created_at,
          linkedGoals,
          linkedRubricItems,
          tags: note.custom_tags || []
        };
      });

      setEnrichedNotes(enriched);
    };

    enrichNotes();
  }, [notes, goals, evidenceLinks]);

  const filteredCards = enrichedCards.filter(card => {
    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      if (!card.title.toLowerCase().includes(query) && 
          !card.summary.toLowerCase().includes(query)) {
        return false;
      }
    }

    // Link status filter
    const isLinked = card.linkedGoals.length > 0 || card.linkedRubricItems.length > 0;
    if (filterType === 'linked' && !isLinked) {
      return false;
    }
    if (filterType === 'unlinked' && isLinked) {
      return false;
    }

    return true;
  });

  const filteredNotes = enrichedNotes.filter(note => {
    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      if (!note.title.toLowerCase().includes(query) && 
          !note.content.toLowerCase().includes(query)) {
        return false;
      }
    }

    // Link status filter
    const isLinked = note.linkedGoals.length > 0 || note.linkedRubricItems.length > 0;
    if (filterType === 'linked' && !isLinked) {
      return false;
    }
    if (filterType === 'unlinked' && isLinked) {
      return false;
    }

    return true;
  });

  const handleLinkEvidence = (itemId: string, itemType: 'career_card' | 'note') => {
    setSelectedItemId(itemId);
    setSelectedItemType(itemType);
  };

  const handleLinkingComplete = () => {
    // Refresh evidence links
    const fetchLinks = async () => {
      const { data } = await supabase
        .from('content_links')
        .select('*')
        .in('source_type', ['career_card', 'note'])
        .in('target_type', ['goal', 'rubric_item', 'rubric_criterion']);
      
      setEvidenceLinks(data || []);
    };
    fetchLinks();
    setSelectedItemId(null);
    setSelectedItemType(null);
  };

  const activeEvidence = activeTab === 'cards' ? filteredCards : filteredNotes;
  const totalCount = activeTab === 'cards' ? enrichedCards.length : enrichedNotes.length;
  const linkedCount = activeTab === 'cards' 
    ? enrichedCards.filter(e => e.linkedGoals.length > 0 || e.linkedRubricItems.length > 0).length
    : enrichedNotes.filter(e => e.linkedGoals.length > 0 || e.linkedRubricItems.length > 0).length;
  const unlinkedCount = totalCount - linkedCount;

  return (
    <div className="space-y-4">
      {/* Header */}
      <div>
        <h3 className={`text-lg font-semibold ${textColor} mb-1`}>Evidence Library</h3>
        <p className={`text-sm ${mutedTextColor}`}>
          All your documented achievements and experiences
        </p>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 p-1 bg-gray-100 dark:bg-gray-800 rounded-lg">
        <button
          onClick={() => setActiveTab('cards')}
          className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
            activeTab === 'cards'
              ? `${tabActiveBg} ${textColor}`
              : `${mutedTextColor} hover:text-gray-700 dark:hover:text-gray-300`
          }`}
        >
          <IoCardOutline className="w-4 h-4" />
          Career Cards
        </button>
        <button
          onClick={() => setActiveTab('notes')}
          className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
            activeTab === 'notes'
              ? `${tabActiveBg} ${textColor}`
              : `${mutedTextColor} hover:text-gray-700 dark:hover:text-gray-300`
          }`}
        >
          <IoDocumentOutline className="w-4 h-4" />
          Notes
        </button>
      </div>

      {/* Search and Filters */}
      <div className="flex gap-3 mb-4">
        {/* Search Bar */}
        <div className="flex-1 relative">
          <IoSearchOutline className={`absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 ${mutedTextColor}`} />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder={`Search ${activeTab === 'cards' ? 'career cards' : 'notes'}...`}
            className={`w-full pl-10 pr-4 py-2 rounded-lg border ${borderColor} ${inputBg} ${textColor} placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500`}
          />
        </div>

        {/* Filter Buttons */}
        <div className="flex gap-2">
          {(['all', 'linked', 'unlinked'] as const).map((filter) => (
            <button
              key={filter}
              onClick={() => setFilterType(filter)}
              className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                filterType === filter
                  ? 'bg-blue-500 text-white'
                  : darkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-200 text-gray-700'
              }`}
            >
              {filter === 'all' ? 'All' : filter === 'linked' ? 'Linked' : 'Unlinked'}
            </button>
          ))}
        </div>
      </div>

      {/* Evidence Stats */}
      <div className="grid grid-cols-3 gap-3 mb-4">
        <div className={`p-3 rounded-lg ${cardBg} text-center`}>
          <p className={`text-2xl font-semibold ${textColor}`}>{totalCount}</p>
          <p className={`text-xs ${mutedTextColor}`}>Total {activeTab === 'cards' ? 'Cards' : 'Notes'}</p>
        </div>
        <div className={`p-3 rounded-lg ${cardBg} text-center`}>
          <p className={`text-2xl font-semibold text-green-500`}>{linkedCount}</p>
          <p className={`text-xs ${mutedTextColor}`}>Linked</p>
        </div>
        <div className={`p-3 rounded-lg ${cardBg} text-center`}>
          <p className={`text-2xl font-semibold text-orange-500`}>{unlinkedCount}</p>
          <p className={`text-xs ${mutedTextColor}`}>Unlinked</p>
        </div>
      </div>

      {/* Loading State */}
      {(activeTab === 'cards' ? cardsLoading : !notes) && (
        <div className="flex items-center justify-center h-32">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
        </div>
      )}

      {/* Error State */}
      {(activeTab === 'cards' ? cardsError : notesError) && (
        <div className="flex items-center justify-center h-32">
          <div className="text-red-500 text-center">
            <p className="mb-2">Failed to load {activeTab}</p>
            <button 
              onClick={() => activeTab === 'cards' ? refreshCards() : window.location.reload()}
              className="text-sm text-blue-500 hover:text-blue-600"
            >
              Try again
            </button>
          </div>
        </div>
      )}

      {/* Evidence List */}
      {!(activeTab === 'cards' ? cardsLoading : !notes) && !(activeTab === 'cards' ? cardsError : notesError) && (
        <div className="space-y-3">
          {activeTab === 'cards' ? (
            // Career Cards List
            filteredCards.map((card, index) => (
              <motion.div
                key={card.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.2, delay: index * 0.05 }}
                className={`p-4 rounded-lg border ${borderColor} ${cardBg} hover:shadow-md transition-shadow`}
              >
                <div className="flex items-start gap-3">
                  {/* Type Icon */}
                  <div className={`p-2 rounded-lg ${darkMode ? 'bg-blue-900/20 text-blue-400' : 'bg-blue-100 text-blue-600'}`}>
                    <IoCardOutline className="w-5 h-5" />
                  </div>

                  {/* Content */}
                  <div className="flex-1">
                    <h4 className={`font-medium ${textColor} mb-1`}>{card.title}</h4>
                    <p className={`text-xs ${mutedTextColor} mb-1`}>
                      {new Date(card.created_at).toLocaleDateString()} • Career Card
                    </p>
                    {card.summary && (
                      <p className={`text-sm ${mutedTextColor} mb-2`}>{card.summary}</p>
                    )}

                    {/* Linked Items */}
                    <div className="flex flex-wrap gap-2">
                      {card.linkedGoals.map((goal) => (
                        <span
                          key={goal.id}
                          className={`text-xs px-2 py-1 rounded-full ${
                            darkMode ? 'bg-yellow-900/20 text-yellow-400' : 'bg-yellow-100 text-yellow-700'
                          }`}
                          title={goal.text}
                        >
                          🎯 {goal.text.length > 20 ? goal.text.substring(0, 20) + '...' : goal.text}
                        </span>
                      ))}
                      {card.linkedRubricItems.map((rubric, idx) => (
                        <span
                          key={`card-rubric-${idx}`}
                          className={`text-xs px-2 py-1 rounded-full ${
                            darkMode ? 'bg-green-900/20 text-green-400' : 'bg-green-100 text-green-700'
                          }`}
                          title={rubric.text}
                        >
                          📋 {rubric.text.length > 20 ? rubric.text.substring(0, 20) + '...' : rubric.text}
                        </span>
                      ))}
                      {card.linkedGoals.length === 0 && card.linkedRubricItems.length === 0 && (
                        <span className={`text-xs ${mutedTextColor}`}>
                          Not linked to any goals or rubric items
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Actions */}
                  <button 
                    onClick={() => handleLinkEvidence(card.id, 'career_card')}
                    className="text-sm px-3 py-1 rounded bg-blue-500 hover:bg-blue-600 text-white transition-colors flex items-center gap-1"
                  >
                    <IoLinkOutline className="w-4 h-4" />
                    Link
                  </button>
                </div>
              </motion.div>
            ))
          ) : (
            // Notes List
            filteredNotes.map((note, index) => (
              <motion.div
                key={note.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.2, delay: index * 0.05 }}
                className={`p-4 rounded-lg border ${borderColor} ${cardBg} hover:shadow-md transition-shadow`}
              >
                <div className="flex items-start gap-3">
                  {/* Type Icon */}
                  <div className={`p-2 rounded-lg ${darkMode ? 'bg-green-900/20 text-green-400' : 'bg-green-100 text-green-600'}`}>
                    <IoDocumentOutline className="w-5 h-5" />
                  </div>

                  {/* Content */}
                  <div className="flex-1">
                    <h4 className={`font-medium ${textColor} mb-1`}>{note.title}</h4>
                    <p className={`text-xs ${mutedTextColor} mb-1`}>
                      {new Date(note.created_at).toLocaleDateString()} • Note
                    </p>
                    {note.content && (
                      <p className={`text-sm ${mutedTextColor} mb-2 line-clamp-2`}>
                        {note.content}
                      </p>
                    )}

                    {/* Linked Items */}
                    <div className="flex flex-wrap gap-2">
                      {note.linkedGoals.map((goal) => (
                        <span
                          key={goal.id}
                          className={`text-xs px-2 py-1 rounded-full ${
                            darkMode ? 'bg-yellow-900/20 text-yellow-400' : 'bg-yellow-100 text-yellow-700'
                          }`}
                          title={goal.text}
                        >
                          🎯 {goal.text.length > 20 ? goal.text.substring(0, 20) + '...' : goal.text}
                        </span>
                      ))}
                      {note.linkedRubricItems.map((rubric, idx) => (
                        <span
                          key={`note-rubric-${idx}`}
                          className={`text-xs px-2 py-1 rounded-full ${
                            darkMode ? 'bg-green-900/20 text-green-400' : 'bg-green-100 text-green-700'
                          }`}
                          title={rubric.text}
                        >
                          📋 {rubric.text.length > 20 ? rubric.text.substring(0, 20) + '...' : rubric.text}
                        </span>
                      ))}
                      {note.linkedGoals.length === 0 && note.linkedRubricItems.length === 0 && (
                        <span className={`text-xs ${mutedTextColor}`}>
                          Not linked to any goals or rubric items
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Actions */}
                  <button 
                    onClick={() => handleLinkEvidence(note.id, 'note')}
                    className="text-sm px-3 py-1 rounded bg-blue-500 hover:bg-blue-600 text-white transition-colors flex items-center gap-1"
                  >
                    <IoLinkOutline className="w-4 h-4" />
                    Link
                  </button>
                </div>
              </motion.div>
            ))
          )}
        </div>
      )}

      {/* Empty State */}
      {activeEvidence.length === 0 && (
        <div className={`text-center py-8 ${mutedTextColor}`}>
          <IoDocumentTextOutline className="w-12 h-12 mx-auto mb-3 opacity-50" />
          <p className="text-sm">
            {searchQuery || filterType !== 'all' 
              ? `No ${activeTab === 'cards' ? 'career cards' : 'notes'} match your filters` 
              : `No ${activeTab === 'cards' ? 'career cards' : 'notes'} documented yet`}
          </p>
        </div>
      )}

      {/* Help Text */}
      <div className={`text-sm ${mutedTextColor} mt-6 p-4 ${cardBg} rounded-lg`}>
        <p className="font-medium mb-1">📚 Building Your Evidence Library</p>
        <p>
          Every career card and note you create becomes evidence. Link them to goals and 
          rubric items to show how your work supports your objectives and meets evaluation criteria.
        </p>
      </div>

      {/* Evidence Linking Modal - Updated to handle both types */}
      {selectedItemId && selectedItemType && (
        <EvidenceLinkingModal
          cardId={selectedItemId}
          sourceType={selectedItemType}
          focusId={focus.id}
          activeRubric={activeRubric}
          isOpen={!!selectedItemId}
          onClose={() => {
            setSelectedItemId(null);
            setSelectedItemType(null);
          }}
          onComplete={handleLinkingComplete}
        />
      )}
    </div>
  );
};

export default EvidenceLibraryTab;